import React, { useEffect, useMemo, useState } from "react";
import DocumentType from "./DocumentType";

// ==== Types
type ExtraFieldType = "text" | "textarea" | "number" | "date";

type ExtraField = {
  key: string;
  label: string;
  type: ExtraFieldType;
  placeholder?: string;
};

type MethodTemplate = {
  id: string;
  label: string;
  description?: string;
  sections: string[];
  defaults?: { tone?: string };
  extraFields?: ExtraField[];
};

// ==== Templates de base (réutilisables)
const BASE_TEMPLATES: Record<string, MethodTemplate> = {
  BUSINESS_PLAN: {
    id: "BUSINESS_PLAN",
    label: "Business Plan (BMC/PESTEL)",
    description:
      "Résumé → Marché → Offre → Modèle éco → Go-to-market → Ops → Financier → Risques.",
    sections: [
      "Résumé exécutif",
      "Analyse du marché",
      "Proposition de valeur",
      "Modèle économique",
      "Stratégie commerciale",
      "Organisation & opérations",
      "Plan financier",
      "Risques & atténuation",
    ],
    defaults: { tone: "Formel" },
    extraFields: [
      { key: "horizon", label: "Horizon (années)", type: "number" },
      { key: "secteur", label: "Secteur", type: "text" },
    ],
  },
  MARKET_STUDY: {
    id: "MARKET_STUDY",
    label: "Étude de marché (IMRAD light)",
    sections: [
      "Objectifs",
      "Contexte & hypothèses",
      "Méthodologie",
      "Résultats",
      "Analyse",
      "Limites",
      "Recommandations",
    ],
    defaults: { tone: "Informatif" },
    extraFields: [
      { key: "perimetre", label: "Périmètre géographique", type: "text" },
      { key: "echantillon", label: "Taille d’échantillon", type: "number" },
    ],
  },
  ADVOCACY: {
    id: "ADVOCACY",
    label: "Plaidoyer (Problème → Preuves → Cibles)",
    sections: [
      "Problème public",
      "Preuves & données",
      "Cibles & alliés",
      "Messages clés",
      "Plan d’action",
      "Indicateurs",
      "Risques & atténuation",
    ],
    defaults: { tone: "Persuasif" },
    extraFields: [
      { key: "decisionnaire", label: "Décideur visé", type: "text" },
      { key: "delai", label: "Échéance", type: "date" },
    ],
  },
  STRATEGIC_PLAN: {
    id: "STRATEGIC_PLAN",
    label: "Plan stratégique (Diagnostic → Axes → KPI)",
    sections: [
      "Vision & mission",
      "Diagnostic (SWOT/PESTEL)",
      "Axes stratégiques",
      "Objectifs SMART",
      "Indicateurs (KPI)",
      "Gouvernance & ressources",
      "Plan de mise en œuvre",
      "Risques",
    ],
    defaults: { tone: "Formel" },
    extraFields: [{ key: "periode", label: "Période (ex. 2025–2027)", type: "text" }],
  },
  ANNUAL_WORKPLAN: {
    id: "ANNUAL_WORKPLAN",
    label: "Plan d’activité (RACI & calendrier)",
    sections: [
      "Objectifs & résultats",
      "Activités",
      "Calendrier",
      "RACI",
      "Budget & ressources",
      "Indicateurs",
      "Risques",
    ],
    defaults: { tone: "Informatif" },
    extraFields: [
      { key: "annee", label: "Année", type: "number" },
      { key: "budget", label: "Budget total", type: "text" },
    ],
  },
  TECH_PROPOSAL: {
    id: "TECH_PROPOSAL",
    label: "Proposition technique",
    sections: [
      "Résumé & compréhension",
      "Approche & méthodologie",
      "Plan de travail",
      "Livrables",
      "Équipe & rôles",
      "Expériences",
      "Qualité & risques",
    ],
    defaults: { tone: "Technique" },
    extraFields: [
      { key: "bailleur", label: "Bailleur/Client", type: "text" },
      { key: "reference", label: "Référence appel", type: "text" },
    ],
  },
  FIN_PROPOSAL: {
    id: "FIN_PROPOSAL",
    label: "Proposition financière",
    sections: [
      "Hypothèses",
      "Budget détaillé",
      "Justification des coûts",
      "Conditions de paiement",
      "Taxes & exclusions",
      "Risques financiers",
    ],
    defaults: { tone: "Technique" },
    extraFields: [{ key: "devise", label: "Devise (GNF/EUR)", type: "text" }],
  },
  ACTIVITY_REPORT: {
    id: "ACTIVITY_REPORT",
    label: "Rapport d’activité",
    sections: [
      "Résumé exécutif",
      "Contexte & objectifs",
      "Activités réalisées",
      "Résultats & indicateurs",
      "Difficultés & solutions",
      "Finances (si applicable)",
      "Leçons & prochaines étapes",
    ],
    defaults: { tone: "Informatif" },
    extraFields: [{ key: "periode", label: "Période couverte", type: "text" }],
  },
  MEETING_MINUTES: {
    id: "MEETING_MINUTES",
    label: "Compte rendu (Décisions → Actions)",
    sections: ["Objet & date", "Participants", "Points", "Décisions", "Actions (qui/quand)"],
    defaults: { tone: "Informatif" },
    extraFields: [{ key: "date", label: "Date de réunion", type: "date" }],
  },
  GENERIC_REPORT: {
    id: "GENERIC_REPORT",
    label: "Rapport (IMRAD)",
    sections: ["Introduction", "Méthodologie", "Résultats", "Analyse", "Conclusion", "Annexes"],
    defaults: { tone: "Informatif" },
  },
  LETTER: {
    id: "LETTER",
    label: "Courrier",
    sections: ["En-tête", "Objet", "Destinataire", "Corps", "Politesse", "Pièces jointes"],
    defaults: { tone: "Formel" },
    extraFields: [
      { key: "destinataire", label: "Destinataire", type: "text" },
      { key: "objet", label: "Objet", type: "text" },
    ],
  },
  TDR: {
    id: "TDR",
    label: "TDR",
    sections: [
      "Contexte",
      "Objectifs",
      "Résultats attendus",
      "Périmètre & méthodologie",
      "Livrables",
      "Profil requis",
      "Calendrier",
      "Budget (si applicable)",
      "Modalités de candidature",
    ],
    defaults: { tone: "Technique" },
  },
  RTCF: {
    id: "RTCF",
    label: "RTCF (Résultats → Tâches → Calendrier → Financement)",
    sections: ["Résultats", "Tâches", "Calendrier", "Budget/Financement", "Risques"],
    defaults: { tone: "Informatif" },
  },
};

function getTemplatesFor(category: string, subType: string): MethodTemplate[] {
  const s = (subType || "").toLowerCase();
  if (category === "Document Stratégiques") {
    if (s.includes("business")) return [BASE_TEMPLATES.BUSINESS_PLAN, BASE_TEMPLATES.STRATEGIC_PLAN, BASE_TEMPLATES.ADVOCACY];
    if (s.includes("marché")) return [BASE_TEMPLATES.MARKET_STUDY, BASE_TEMPLATES.BUSINESS_PLAN];
    if (s.includes("plaidoyer")) return [BASE_TEMPLATES.ADVOCACY, BASE_TEMPLATES.RTCF];
    if (s.includes("stratégique")) return [BASE_TEMPLATES.STRATEGIC_PLAN, BASE_TEMPLATES.RTCF];
    return [BASE_TEMPLATES.GENERIC_REPORT];
  }
  if (category === "Opérationnels") {
    if (s.includes("activité") && s.includes("annuel")) return [BASE_TEMPLATES.ANNUAL_WORKPLAN, BASE_TEMPLATES.RTCF];
    if (s.includes("technique")) return [BASE_TEMPLATES.TECH_PROPOSAL];
    if (s.includes("financi")) return [BASE_TEMPLATES.FIN_PROPOSAL];
    if (s.includes("rapport")) return [BASE_TEMPLATES.ACTIVITY_REPORT];
    return [BASE_TEMPLATES.GENERIC_REPORT];
  }
  if (category === "Normatifs") {
    if (s.includes("compte") || s.includes("rendu")) return [BASE_TEMPLATES.MEETING_MINUTES];
    if (s.includes("courrier")) return [BASE_TEMPLATES.LETTER];
    if (s.includes("tdr")) return [BASE_TEMPLATES.TDR];
    return [BASE_TEMPLATES.GENERIC_REPORT];
  }
  if (category === "Techniques") {
    if (s.includes("manuel")) return [BASE_TEMPLATES.TECH_PROPOSAL];
    if (s.includes("spécification") || s.includes("specification")) return [BASE_TEMPLATES.TDR];
    if (s.includes("documentation")) return [BASE_TEMPLATES.GENERIC_REPORT];
    if (s.includes("procédure") || s.includes("procedure")) return [BASE_TEMPLATES.GENERIC_REPORT];
    return [BASE_TEMPLATES.GENERIC_REPORT];
  }
  return [BASE_TEMPLATES.GENERIC_REPORT];
}

interface GuidedPromptOptionsProps {
  category: string;
  setCategory: (v: string) => void;
  subType: string;
  setSubType: (v: string) => void;
  onDocTypeChange: (d: { category: string; subType: string; domain?: string; domainId?: number; documentTypeId?: number; documentSubtypeId?: number }) => void;

  objective: string;
  setObjective: (v: string) => void;
  targetAudience: string;
  setTargetAudience: (v: string) => void;
  sections: string[];
  setSections: (s: string[]) => void;
  tone: string;
  setTone: (t: string) => void;

  onGenerate: () => void;
  onExtrasChange?: (extras: Record<string, string>) => void;
}

const GuidedPromptOptions: React.FC<GuidedPromptOptionsProps> = ({
  category,
  setCategory,
  subType,
  setSubType,
  onDocTypeChange,
  objective,
  setObjective,
  targetAudience,
  setTargetAudience,
  sections,
  setSections,
  tone,
  setTone,
  onGenerate,
  onExtrasChange,
}) => {
  const hasDocType = Boolean(category && subType);
  const availableMethods = useMemo(
    () => (hasDocType ? getTemplatesFor(category, subType) : []),
    [category, subType, hasDocType]
  );
  const showMethodPicker = hasDocType && availableMethods.length > 1;

  const [methodId, setMethodId] = useState<string | undefined>(undefined);
  const [extras, setExtras] = useState<Record<string, string>>({});

  // init méthode/sections/tone uniquement quand sous-type choisi
  useEffect(() => {
    if (!hasDocType) {
      setMethodId(undefined);
      setExtras({});
      return;
    }
    const first = availableMethods[0];
    setMethodId(first?.id);
    if (first) {
      setSections(first.sections);
      if (first.defaults?.tone) setTone(first.defaults.tone);
      const init: Record<string, string> = {};
      first.extraFields?.forEach((f) => (init[f.key] = ""));
      setExtras(init);
      onExtrasChange?.(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDocType, category, subType]);

  const selectedMethod =
    hasDocType && methodId
      ? availableMethods.find((m) => m.id === methodId) || availableMethods[0]
      : undefined;

  const applyRecommendedSections = () => {
    if (selectedMethod) setSections(selectedMethod.sections);
  };

  const handleSectionToggle = (section: string) => {
    setSections(
      sections.includes(section)
        ? sections.filter((s) => s !== section)
        : [...sections, section]
    );
  };

  const handleExtraChange = (key: string, val: string) => {
    const next = { ...extras, [key]: val };
    setExtras(next);
    onExtrasChange?.(next);
  };

  return (
    <div className="bg-white p-4 rounded-xl shadow-md border border-gray-200 mb-4 space-y-4">
      {/* Étape 1 — Type de document (toujours visible) */}
      <div>
        <DocumentType
          category={category}
          setCategory={setCategory}
          subType={subType}
          setSubType={setSubType}
          onChange={({ category: newCat, subType: newSub, domain, domainId, documentTypeId, documentSubtypeId }) => {
            onDocTypeChange({ 
              category: newCat || "", 
              subType: newSub || "",
              domain,
              domainId,
              documentTypeId,
              documentSubtypeId
            });
          }}
        />
        {category && !subType && (
          <p className="text-xs text-red-500 mt-1">
            Sous-type obligatoire. Les options suivantes apparaîtront après votre sélection.
          </p>
        )}
      </div>

      {/* Étape 2 — Méthode (affichée seulement si > 1) */}
      {showMethodPicker && (
        <div>
          <label className="block font-semibold mb-1 text-sm">Méthode / gabarit recommandé</label>
          <select
            className="w-full p-2 rounded-lg border text-sm"
            value={methodId}
            onChange={(e) => {
              const id = e.target.value;
              setMethodId(id);
              const tmpl = availableMethods.find((m) => m.id === id);
              if (tmpl) {
                setSections(tmpl.sections);
                if (tmpl.defaults?.tone) setTone(tmpl.defaults.tone);
                const init: Record<string, string> = {};
                tmpl.extraFields?.forEach((f) => (init[f.key] = extras[f.key] ?? ""));
                setExtras(init);
                onExtrasChange?.(init);
              }
            }}
          >
            {availableMethods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
          {selectedMethod?.description && (
            <p className="text-xs text-gray-500 mt-1">{selectedMethod.description}</p>
          )}
          <button
            type="button"
            onClick={applyRecommendedSections}
            className="mt-1 text-xs underline text-teal-600"
          >
            Réappliquer les sections recommandées
          </button>
        </div>
      )}

      {/* Étape 3 — Champs additionnels (seulement si sous-type choisi) */}
      {hasDocType && selectedMethod?.extraFields && selectedMethod.extraFields.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {selectedMethod.extraFields.map((f) => (
            <div key={f.key} className="flex flex-col">
              <label className="text-xs font-semibold mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  className="w-full p-2 rounded-lg border text-sm"
                  rows={2}
                  placeholder={f.placeholder}
                  value={extras[f.key] ?? ""}
                  onChange={(e) => handleExtraChange(f.key, e.target.value)}
                />
              ) : (
                <input
                  className="w-full p-2 rounded-lg border text-sm"
                  type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                  placeholder={f.placeholder}
                  value={extras[f.key] ?? ""}
                  onChange={(e) => handleExtraChange(f.key, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Étape 4 — Objectif & Public cible (compacts) */}
      {hasDocType && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="mb-2">
            <label className="block font-semibold mb-1 text-sm">Objectif</label>
            <textarea
              className="w-full p-2 rounded-lg border text-sm"
              rows={2}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
            />
          </div>
          <div className="mb-2">
            <label className="block font-semibold mb-1 text-sm">Public cible</label>
            <input
              className="w-full p-2 rounded-lg border text-sm"
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Étape 5 — Sections */}
      {hasDocType && selectedMethod && (
        <div className="mb-2">
          <label className="block font-semibold mb-2 text-sm">Sections</label>
          <div className="grid grid-cols-2 gap-2">
            {selectedMethod.sections.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sections.includes(s)}
                  onChange={() => handleSectionToggle(s)}
                />
                <span className="truncate">{s}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Étape 6 — Ton */}
      {hasDocType && (
        <div className="mb-1">
          <label className="block font-semibold mb-2 text-sm">Ton</label>
          <div className="flex flex-wrap gap-3 text-sm">
            {["Formel", "Informel", "Persuasif", "Informatif", "Technique"].map((t) => (
              <label key={t} className="flex items-center gap-2">
                <input type="radio" name="tone" checked={tone === t} onChange={() => setTone(t)} />
                <span>{t}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Étape 7 — Générer */}
      {hasDocType && (
        <button
          onClick={onGenerate}
          className="w-full bg-teal-600 text-white py-2 rounded-lg text-sm"
        >
          Générer le prompt
        </button>
      )}
    </div>
  );
};

export default GuidedPromptOptions;
